import type { Column, GridApi } from 'ag-grid-community';
import { excelDay, exportCellValue, type ExportColumn, type ExportExcelCell, type ExportRow, type ExportRowOrder } from './dataExport';
import { buildRecordPath } from './recordTargets';

/**
 * REG-874: the Export dialog offers the grid's own columns, in the order the
 * user sees them, and writes what each cell shows (names, $ amounts, dates),
 * not the raw database values behind it.
 */

const AUDIT_FIELDS = new Set(['created_by', 'updated_by', 'created_at', 'updated_at']);

/**
 * Plumbing columns: record ids, search vectors, audit stamps and the related
 * "_ref" lookups. They are offered only when the user has them on screen,
 * because the default selection is exactly the visible columns.
 */
export function isInternalExportField(key: string): boolean {
  return key === 'id' || key === 'full_text_search' || AUDIT_FIELDS.has(key)
    || key.endsWith('_ref') || key.endsWith('_rel') || key.endsWith('_label');
}

/** number = thousands separators (20,274.50); decimal = none (12.50). */
export type ExcelKind = 'currency' | 'percent' | 'number' | 'decimal' | 'date' | 'timestamp_date';

/** Excel number formats matching the grid's own formatting for each kind. */
export function excelCell(
  kind: ExcelKind, value: unknown, precision = 2, dateFormat = 'm/d/yyyy', maxPrecision = precision,
): ExportExcelCell | null {
  if (value === null || value === undefined || value === '') return null;
  // Intl shows between precision and maxPrecision decimals ($372.5, $50,000); match it per value.
  const places = (n: number) => {
    let used = 0;
    while (used < maxPrecision && Math.round(n * 10 ** used) / 10 ** used !== n) used++;
    return Math.max(precision, used);
  };
  const decimalsFor = (n: number) => (places(n) > 0 ? `.${'0'.repeat(places(n))}` : '');
  if (kind === 'date' || kind === 'timestamp_date') {
    // A SQL date is a calendar day; a timestamp shows as the viewer's local day.
    const text = String(value);
    const plain = kind === 'date' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(text) : null;
    if (plain) return { v: excelDay(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3])), z: dateFormat };
    const at = new Date(text);
    if (Number.isNaN(at.getTime())) return null;
    return { v: excelDay(at.getFullYear(), at.getMonth(), at.getDate()), z: dateFormat };
  }
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const decimals = decimalsFor(number);
  if (kind === 'currency') return { v: number, z: `"$"#,##0${decimals}` };
  // The grid shows the stored number with a % sign (12 -> 12.00%), not a fraction.
  if (kind === 'percent') return { v: number, z: `0${decimals}"%"` };
  if (kind === 'number') return { v: number, z: `#,##0${decimals}` };
  return { v: number, z: `0${decimals}` };
}

/** A lone dash is how grids draw an empty cell; in a file the cell is just empty. */
const EMPTY_PLACEHOLDER = /^[-\u2013\u2014]$/;

function cellParams(api: GridApi, column: Column, row: ExportRow) {
  const colDef = column.getColDef();
  return { data: row, node: null, colDef, column, api, context: api.getGridOption('context') };
}

/** The value the grid sorts and formats for this row in this column (its valueGetter, else the field). */
function gridCellValue(api: GridApi, column: Column, row: ExportRow): unknown {
  const colDef = column.getColDef();
  return typeof colDef.valueGetter === 'function'
    ? colDef.valueGetter({ ...cellParams(api, column, row), getValue: (key: string) => row[key] } as never)
    : row[colDef.field ?? column.getColId()];
}

/** The text the grid shows for this row in this column, using the column's own getter and formatter. */
export function gridCellText(api: GridApi, column: Column, row: ExportRow): string {
  const colDef = column.getColDef();
  const field = colDef.field ?? column.getColId();
  const base = cellParams(api, column, row);
  const value = gridCellValue(api, column, row);
  if (typeof colDef.valueFormatter === 'function') {
    const text = colDef.valueFormatter({ ...base, value } as never);
    return text == null || EMPTY_PLACEHOLDER.test(String(text).trim()) ? '' : String(text);
  }
  const raw = value === undefined ? exportCellValue(row, field) : value;
  if (raw == null) return '';
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (typeof raw === 'object') return exportCellValue({ value: raw }, 'value').toString();
  return String(raw);
}

/** What a column definition tells the Export dialog (set in `colDef.context.export`). */
export interface ColumnExportHints {
  /** Row fields the column's formatter reads besides its own key. */
  fields?: string[];
  /**
   * dateFormat: the Excel date format matching the grid's (default m/d/yyyy).
   * maxPrecision: the most decimals the grid shows (default precision).
   */
  excel?: { kind: ExcelKind; precision?: number; maxPrecision?: number; dateFormat?: string };
  /** A control, not data (row buttons): never offered. */
  exclude?: boolean;
  /** The cell opens this table's record whose id is in `idField` (default: the column's key). */
  link?: { table: string; idField?: string };
}

export function columnExportHints(column: Column): ColumnExportHints {
  return (column.getColDef().context as { export?: ColumnExportHints } | undefined)?.export ?? {};
}

/** The absolute address of a record, from the route metadata (no navigator context needed). */
export function absoluteRecordUrl(
  tableToRouteName: Record<string, string> | undefined, tableName: string, id: unknown,
): string | null {
  if (id === null || id === undefined || id === '') return null;
  return `${window.location.origin}${buildRecordPath(tableToRouteName?.[tableName] || tableName, { tableName, id: String(id) })}`;
}

/**
 * The grid's columns in display order. Columns with no data field or header
 * (row actions, checkboxes, "view record") and hidden plumbing columns are left
 * out; anything on screen is offered and ticked.
 */
export function gridExportColumns<T>(
  api: GridApi<T>,
  include: (key: string, column: Column) => boolean,
  tableToRouteName: Record<string, string> | undefined,
): ExportColumn[] {
  return (api.getAllGridColumns() ?? []).flatMap((column) => {
    const colDef = column.getColDef();
    // Workspace grids name computed columns by colId (a valueGetter) rather than a field.
    const key = colDef.field ?? colDef.colId;
    const visible = column.isVisible();
    if (!key || key.startsWith('__') || key.startsWith('ag-Grid') || !colDef.headerName || !include(key, column)) return [];
    const hints = columnExportHints(column);
    if (hints.exclude) return [];
    if (!visible && isInternalExportField(key)) return [];
    const { excel, link } = hints;
    const idField = link?.idField ?? key;
    return [{
      key,
      label: colDef.headerName || key,
      visible,
      fields: [key, ...(hints.fields ?? []), ...(link && idField !== key ? [idField] : [])],
      text: (row: ExportRow) => gridCellText(api as GridApi, column, row),
      ...(excel ? { excel: (row: ExportRow) => excelCell(excel.kind, row[key], excel.precision, excel.dateFormat, excel.maxPrecision) } : {}),
      ...(link ? { href: (row: ExportRow) => absoluteRecordUrl(tableToRouteName, link.table, row[idField]) } : {}),
    }];
  });
}

/** ag-Grid's default comparator: empty values first, then plain less-than. */
function gridDefaultCompare(a: unknown, b: unknown): number {
  if (a == null || b == null) return a == null ? (b == null ? 0 : -1) : 1;
  return (a as number) < (b as number) ? -1 : (a as number) > (b as number) ? 1 : 0;
}

/**
 * The grid's sorted columns in the order ag-Grid applies them: by sort index,
 * columns without one after those with one (column order breaks ties).
 */
export function sortedColumnStates(api: GridApi) {
  const position = (index: number | null | undefined) => index ?? Number.MAX_SAFE_INTEGER;
  return (api.getColumnState() ?? [])
    .filter((state) => state.sort === 'asc' || state.sort === 'desc')
    .sort((a, b) => position(a.sortIndex) - position(b.sortIndex));
}

/**
 * A client-side grid's row order: its sorted columns as ag-Grid compares them
 * (each column's comparator, descending reversed), ties in the order rows came.
 * Undefined when nothing is sorted: the rows already come in the grid's order.
 */
export function clientGridOrder(api: GridApi): ExportRowOrder | undefined {
  const keys = sortedColumnStates(api)
    .flatMap((state) => {
      const column = api.getColumn(state.colId);
      return column ? [{ column, desc: state.sort === 'desc' }] : [];
    });
  if (!keys.length) return undefined;
  return {
    fields: [],
    sort: (rows) => rows
      .map((row, index) => ({ row, index, values: keys.map(({ column }) => gridCellValue(api, column, row)) }))
      .sort((a, b) => {
        for (const [i, { column, desc }] of keys.entries()) {
          const comparator = column.getColDef().comparator;
          const result = comparator
            ? comparator(a.values[i], b.values[i], { data: a.row } as never, { data: b.row } as never, desc)
            : gridDefaultCompare(a.values[i], b.values[i]);
          if (result) return desc ? -result : result;
        }
        return a.index - b.index;
      })
      .map(({ row }) => row),
  };
}

/** One ORDER BY term of a server grid's query; `field` may be a JSON path ("merchant_rel.label"). */
export interface ServerSortKey {
  field: string;
  desc: boolean;
}

// The databases sort text with en_US rules, but glibc versions differ: prod
// (Postgres 17) orders spaces and punctuation before digits and letters, like
// the browser's en-US collator; dev (Postgres 15) ignores them. The rows the
// grid already loaded, in the server's order, decide which one applies
// (dev's older rules match neither exactly, so the closer one wins).
const TEXT_COLLATORS = [new Intl.Collator('en-US'), new Intl.Collator('en-US', { ignorePunctuation: true })];
const SQL_DATE_TIME = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)?)?$/;

function compareSqlValues(a: unknown, b: unknown, collator: Intl.Collator): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  const left = String(a);
  const right = String(b);
  if (SQL_DATE_TIME.test(left) && SQL_DATE_TIME.test(right)) return Date.parse(left) - Date.parse(right);
  return collator.compare(left, right) || (left < right ? -1 : left > right ? 1 : 0);
}

function rowPath(row: ExportRow, field: string): unknown {
  return field.split(/\.|->>/).reduce<unknown>((value, part) => (value && typeof value === 'object' ? (value as ExportRow)[part] : undefined), row);
}

function compareRows(a: ExportRow, b: ExportRow, keys: ServerSortKey[], collator: Intl.Collator): number {
  for (const { field, desc } of keys) {
    const left = rowPath(a, field);
    const right = rowPath(b, field);
    if (left == null || right == null) {
      if (left == null && right == null) continue;
      return left == null ? 1 : -1;
    }
    const result = compareSqlValues(left, right, collator);
    if (result) return desc ? -result : result;
  }
  return 0;
}

/**
 * A server grid's row order, as its query sorts: each key with NULLs last
 * (the grids ask for that in both directions), keys in turn. Callers end
 * with the id tie-breaker their query uses. `loaded` is the rows the grid
 * holds, in the server's order.
 */
export function serverGridOrder(keys: ServerSortKey[], loaded: ExportRow[] = []): ExportRowOrder | undefined {
  if (!keys.length) return undefined;
  const sample = loaded.filter((row) => keys.every(({ field }) => rowPath(row, field) !== undefined));
  // The collator that puts the fewest loaded neighbours out of order (prod's on a tie).
  const misorders = (candidate: Intl.Collator) => sample.filter((row, i) => i > 0 && compareRows(sample[i - 1], row, keys, candidate) > 0).length;
  const collator = TEXT_COLLATORS.reduce((best, candidate) => (misorders(candidate) < misorders(best) ? candidate : best));
  return {
    fields: [...new Set(keys.map(({ field }) => field.split(/\.|->>/)[0]))],
    sort: (rows) => [...rows].sort((a, b) => compareRows(a, b, keys, collator)),
  };
}

/** The rows a server-side grid has loaded, in the order it shows them. */
export function loadedGridRows(api: GridApi): ExportRow[] {
  const rows: ExportRow[] = [];
  api.forEachNode((node) => { if (node.data) rows.push(node.data as ExportRow); });
  return rows;
}
